import React, { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  addDoc,
  increment,
  setDoc,
} from "firebase/firestore";
import { db } from "../../utils/firebase";
import { useAuth } from "../../state/AuthContext";
import { toast } from "react-toastify"; // keep or replace with alert()
import { FaWhatsapp } from 'react-icons/fa';

export default function EmployeeLeadsTable({ onOpenWhatsapp }) {
  const { user } = useAuth();
  const email = user?.email || localStorage.getItem("user");

  const sendLeadToSalesforce = (leadData) => {

  const employeeName =
    user?.displayName ||
    email?.split("@")[0] ||
    "Unknown";

  fetch(
    "https://us-central1-zafflostarter.cloudfunctions.net/createSalesforceEmployeeLead",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        businessName: leadData.businessname,
        phoneNumber: leadData.mobile,
        address: leadData.address,
        email: leadData.contactEmail,
        employeeName,
      }),
    }
  ).catch((err) => {
    console.error("Salesforce lead failed:", err);
  });

};


const [selectedMessage, setSelectedMessage] = useState("");
const [openCategory, setOpenCategory] = React.useState(null);

  // leads + UI state
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [originalEditValues, setOriginalEditValues] = useState(null); // for cancel
  const [currentPage, setCurrentPage] = useState(1);

  // new lead popup
  const [showNewLead, setShowNewLead] = useState(false);
  const [newLead, setNewLead] = useState({
    leadname: "",
    businessname: "",
    city: "",
    address: "",
    mobile: "",
    mobile2: "",
    contactEmail: "",
    leadvalue: "",
    comments: "",
  });

  // local whatsapp preview if parent doesn't pass handler
  const [localWaOpen, setLocalWaOpen] = useState(false);
  const [localWaLead, setLocalWaLead] = useState(null);

  const leadsPerPage = 20;

  // ---------- fetch leads ----------
  const fetchLeads = async () => {
    if (!email) return;
    try {
      const q = query(collection(db, "leads"), where("leadowner", "==", email));
      const snap = await getDocs(q);
      const arr = [];
      snap.forEach((s) => {
        const data = { id: s.id, ...s.data() };
        // show only leads NOT Won or Lost
        if (data.status !== "Won Close" && data.status !== "Lost Dead") {
          arr.push(data);
        }
      });
      // keep local order as returned
      setLeads(arr);
      setFilteredLeads(arr);
      setCurrentPage(1);
    } catch (err) {
      console.error("fetchLeads:", err);
      toast.error?.("Error fetching leads");
    }
  };

  useEffect(() => {
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  // ---------- search (UI-only) ----------
  const handleSearch = () => {
    if (!searchTerm) {
      setFilteredLeads(leads);
      setCurrentPage(1);
      return;
    }
    const term = searchTerm.toLowerCase();
    const filtered = leads.filter((lead) =>
      Object.values(lead).some((val) =>
        String(val ?? "").toLowerCase().includes(term)
      )
    );
    setFilteredLeads(filtered);
    setCurrentPage(1);
  };

  // ---------- inline edit handlers ----------
  const handleEditClick = (lead) => {
    setEditingId(lead.id);
    setEditValues({ ...lead }); // copy
    setOriginalEditValues({ ...lead });
  };

  const handleEditChange = (field, value) => {
    setEditValues((p) => ({ ...p, [field]: value }));
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValues({});
    setOriginalEditValues(null);
  };

const handleSave = async (id) => {
  try {
    const updatedLead = {
      ...editValues,
      updateddate: new Date().toISOString(),
    };

    await updateDoc(doc(db, "leads", id), updatedLead);

    setLeads((prev) => prev.map((l) => (l.id === id ? { ...updatedLead, id } : l)));
    setFilteredLeads((prev) => prev.map((l) => (l.id === id ? { ...updatedLead, id } : l)));
    setEditingId(null);
    setEditValues({});
    setOriginalEditValues(null);

    const prevStatus = originalEditValues?.status;
    const newStatus = updatedLead.status;

    if (prevStatus !== newStatus) {
      if (newStatus === "Contacted") {
        await addPointsToGamification(20);
      } else if (newStatus === "Won Close") {
        await addPointsToGamification(100);
        await updateLeadAnalyticsRevenueAndCount(parseFloat(updatedLead.leadvalue || 0));
      }
    }

    toast.success?.("Lead saved");
  } catch (err) {
    console.error("save lead error", err);
    toast.error?.("Error saving lead");
  }
};


  // ---------- mask/unmask logic ----------
  const [unmaskedRows, setUnmaskedRows] = useState({}); // id -> bool
  const toggleUnmask = (id) => setUnmaskedRows((p) => ({ ...p, [id]: !p[id] }));
  const maskPhone = (num) => {
    if (!num) return "";
    const s = String(num);
    if (s.length <= 4) return "*".repeat(s.length);
    const first = s.slice(0, 2);
    const last = s.slice(-2);
    const middle = "*".repeat(Math.max(0, s.length - 4));
    return `${first}${middle}${last}`;
  };

  // ---------- gamification points updater ----------
  // updates leadAnalytics/<email>.gamification.points safely
  const addPointsToGamification = async (points) => {
    if (!email) return;
    const docRef = doc(db, "leadAnalytics", email);
    try {
      // try update first
      await updateDoc(docRef, { "gamification.points": increment(points) });
    } catch (err) {
      // if update fails (doc not found), create doc with gamification.points
      // setDoc with merge = true to avoid overwriting
      try {
        await setDoc(docRef, { gamification: { points } }, { merge: true });
      } catch (err2) {
        console.error("Failed to set gamification doc:", err2);
      }
    }
  };

// ---------- analytics updater for Closed Won ----------
const updateLeadAnalyticsRevenueAndCount = async (leadValue) => {
  if (!email || isNaN(leadValue)) return;

  const docRef = doc(db, "leadAnalytics", email);
  const now = new Date();
  const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

  try {
    await updateDoc(docRef, {
      totalleadown: increment(1),
      totalrevenuegenerated: increment(leadValue),
      [`monthlyleadown.${monthKey}`]: increment(1),
      [`monthlyrevenuegenerated.${monthKey}`]: increment(leadValue),
    });
  } catch (err) {
    console.error("Error updating lead analytics:", err);

    // If doc doesn’t exist, create a new one
    await setDoc(
      docRef,
      {
        totalleadown: 1,
        totalrevenuegenerated: leadValue,
        monthlyleadown: { [monthKey]: 1 },
        monthlyrevenuegenerated: { [monthKey]: leadValue },
      },
      { merge: true }
    );
  }
};


  // ---------- new lead creation ----------
  const handleSaveNewLead = async () => {
    // validate mandatory: Name, Business Name, City, Mobile, contactEmail, leadvalue
    const required = ["leadname", "businessname", "city", "mobile", "contactEmail", "leadvalue", "address"];
    for (const k of required) {
      if (!String(newLead[k] ?? "").trim()) {
        toast.error?.("Please fill all mandatory fields");
        return;
      }
    }

    try {
      const now = new Date().toISOString();
      const payload = {
        ...newLead,
        leadowner: email,
        status: "New",
        createddate: now,
        updateddate: now,
      };

      await addDoc(collection(db, "leads"), payload);

      sendLeadToSalesforce(newLead);


      // award +20 points to creator (gamification.points)
      await addPointsToGamification(20);

      // reset UI and refetch
      setShowNewLead(false);
      setNewLead({
        leadname: "",
        businessname: "",
        city: "",
        address: "",
        mobile: "",
        mobile2: "",
        contactEmail: "",
        leadvalue: "",
        comments: "",
      });
      toast.success?.("Lead created");
      await fetchLeads();
    } catch (err) {
      console.error("create lead error", err);
      toast.error?.("Error creating lead");
    }
  };

  const [mobileError, setMobileError] = useState("");

const handleMobileChange = (e) => {
  const value = e.target.value;
  setNewLead((p) => ({ ...p, mobile: value }));

  const mobileRegex = /^\d{12}$/;
  if (!mobileRegex.test(value)) {
    setMobileError("Please enter a valid 12-digit mobile number including country code (e.g., 9000000000 or 447900123456).");
  } else {
    setMobileError("");
  }
};

  // ---------- whatsapp / local preview ----------
  const handleWhatsappClick = (lead) => {
    if (typeof onOpenWhatsapp === "function") {
      onOpenWhatsapp(lead);
      return;
    }
    // fallback: local preview modal
    setLocalWaLead(lead);
    setLocalWaOpen(true);
  };

  // ---------- pagination ----------
  const totalPages = Math.max(1, Math.ceil(filteredLeads.length / leadsPerPage));
  const startIndex = (currentPage - 1) * leadsPerPage;
  const currentLeads = filteredLeads.slice(startIndex, startIndex + leadsPerPage);

  // ---------- UI ----------
  return (
    <div className="bg-white rounded-lg shadow p-4 mt-6">
      {/* Header (search + new lead) */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-4">
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Search leads..."
            className="border border-gray-300 rounded-md px-3 py-2 w-64 focus:ring-[#6cbb23] focus:border-[#6cbb23]"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
          <button
            onClick={handleSearch}
            className="bg-[#6cbb23] text-white px-4 py-2 rounded-md hover:bg-[#5aa41d]"
          >
            Search
          </button>
        </div>

        <button
          onClick={() => setShowNewLead(true)}
          className="flex items-center gap-2 bg-[#6cbb23] text-white px-4 py-2 rounded-md hover:bg-[#5aa41d]"
        >
          <span className="text-lg font-bold">+</span> New Lead
        </button>
      </div>

      {/* table */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm border border-gray-200">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="px-3 py-2 border">Lead Name</th>
              <th className="px-3 py-2 border">Business</th>
              <th className="px-3 py-2 border">City</th>
              <th className="px-3 py-2 border">Mobile</th>
              <th className="px-3 py-2 border">Status</th>
              <th className="px-3 py-2 border">Value</th>
              <th className="px-3 py-2 border">Comments</th>
              <th className="px-3 py-2 border">Created</th>
              <th className="px-3 py-2 border">Updated</th>
              <th className="px-3 py-2 border text-center">Actions</th>
            </tr>
          </thead>

          <tbody>
            {currentLeads.map((lead) => (
              <tr
                key={lead.id}
                className={`${lead.status === "New" ? "bg-blue-50" : "bg-white"} hover:bg-gray-50`}
              >
                {/* leadname */}
                <td className="px-3 py-2 border w-40">
                  {editingId === lead.id ? (
                    <input
                      type="text"
                      value={editValues.leadname || ""}
                      onChange={(e) => handleEditChange("leadname", e.target.value)}
                      className="border rounded px-2 py-1 w-full"
                    />
                  ) : (
                    lead.leadname
                  )}
                </td>

                {/* business */}
                <td className="px-3 py-2 border">
                  {editingId === lead.id ? (
                    <input
                      type="text"
                      value={editValues.businessname || ""}
                      onChange={(e) => handleEditChange("businessname", e.target.value)}
                      className="border rounded px-2 py-1 w-full"
                    />
                  ) : (
                    lead.businessname
                  )}
                </td>

                {/* city */}
                <td className="px-3 py-2 border">
                  {editingId === lead.id ? (
                    <input
                      type="text"
                      value={editValues.city || ""}
                      onChange={(e) => handleEditChange("city", e.target.value)}
                      className="border rounded px-2 py-1 w-full"
                    />
                  ) : (
                    lead.city
                  )}
                </td>

                {/* mobile with eye */}
                <td className="px-3 py-2 border flex items-center gap-2">
                  <span className="font-mono">{unmaskedRows[lead.id] ? lead.mobile : maskPhone(lead.mobile)}</span>
                  <button
                    onClick={() => toggleUnmask(lead.id)}
                    className="p-1 rounded hover:bg-gray-100"
                    title={unmaskedRows[lead.id] ? "Hide number" : "View number"}
                  >
                    {/* simple eye icon */}
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </button>
                </td>

                {/* status */}
                <td className="px-3 py-2 border">
                  {editingId === lead.id ? (
                    <select
                      value={editValues.status || "New"}
                      onChange={(e) => handleEditChange("status", e.target.value)}
                      className="border rounded px-2 py-1 w-full"
                    >
                      {["New", "Contacted", "Interested", "Contact Later", "Won Close", "Lost Dead"].map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className={`px-2 py-1 rounded text-sm ${
                      lead.status === "Contacted" ? "bg-yellow-100 text-yellow-800" :
                      lead.status === "Interested" ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-700"
                    }`}>{lead.status}</span>
                  )}
                </td>

                {/* leadvalue */}
                <td className="px-3 py-2 border">
  {editingId === lead.id ? (
    <input
      type="number"
      value={editValues.leadvalue || ""}
      onChange={(e) => handleEditChange("leadvalue", e.target.value)}
      className="border rounded px-2 py-1 w-full"
    />
  ) : (
    lead.leadvalue
  )}
</td>


                {/* comments */}
                <td className="px-3 py-2 border">
                  {editingId === lead.id ? (
                    <input
                      type="text"
                      value={editValues.comments || ""}
                      onChange={(e) => handleEditChange("comments", e.target.value)}
                      className="border rounded px-2 py-1 w-full"
                    />
                  ) : (
                    lead.comments || "—"
                  )}
                </td>

                {/* dates */}
                <td className="px-3 py-2 border text-xs text-gray-500">{lead.createddate || "—"}</td>
                <td className="px-3 py-2 border text-xs text-gray-500">{lead.updateddate || "—"}</td>

                {/* actions */}
                <td className="px-3 py-2 border text-center">
                  {editingId === lead.id ? (
                    <div className="flex justify-center gap-2">
                      <button onClick={() => handleSave(lead.id)} className="text-[#6cbb23] font-semibold px-2 py-1 rounded border border-[#6cbb23]">Save</button>
                      <button onClick={handleCancelEdit} className="px-2 py-1 rounded border">Cancel</button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-3">
                      <button onClick={() => handleEditClick(lead)} className="text-gray-600 hover:text-[#6cbb23]" title="Edit">✏️</button>
                      <button onClick={() => handleWhatsappClick(lead)} className="text-gray-600 hover:text-green-600" title="WhatsApp"> <FaWhatsapp className="text-[#6cbb23] text-xl" /> </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* pagination */}
      <div className="flex justify-end items-center gap-2 mt-4">
        <button
          onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
          disabled={currentPage === 1}
          className="px-3 py-1 border rounded disabled:opacity-50"
        >
          Prev
        </button>
        <div className="px-3 py-1 border rounded bg-gray-50">{currentPage} / {totalPages}</div>
        <button
          onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
          disabled={currentPage === totalPages}
          className="px-3 py-1 border rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>

      {/* new lead popup (inline in same component) */}
      {showNewLead && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-xl p-6 relative">
            <button className="absolute top-3 right-3 text-gray-500 text-xl" onClick={() => setShowNewLead(false)}>✖</button>
            <h3 className="text-xl font-semibold mb-4 text-[#6cbb23]">Create New Lead</h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input placeholder="Lead Name *" value={newLead.leadname} onChange={(e) => setNewLead((p) => ({ ...p, leadname: e.target.value }))} className="border rounded px-3 py-2" />
              <input placeholder="Business Name *" value={newLead.businessname} onChange={(e) => setNewLead((p) => ({ ...p, businessname: e.target.value }))} className="border rounded px-3 py-2" />
              <input placeholder="City *" value={newLead.city} onChange={(e) => setNewLead((p) => ({ ...p, city: e.target.value }))} className="border rounded px-3 py-2" />
              <input placeholder="Address *" value={newLead.address} onChange={(e) => setNewLead((p) => ({ ...p, address: e.target.value }))} className="border rounded px-3 py-2" />
              <input placeholder="Mobile *" value={newLead.mobile} onChange={handleMobileChange} className={`border rounded px-3 py-2 w-full ${mobileError ? "border-red-500" : "border-gray-300"}`}/> {mobileError && (<p className="text-red-500 text-xs mt-1">{mobileError}</p>)}
              <input placeholder="Alt Mobile" value={newLead.mobile2} onChange={(e) => setNewLead((p) => ({ ...p, mobile2: e.target.value }))} className="border rounded px-3 py-2" />
              <input placeholder="Contact Email *" value={newLead.contactEmail} onChange={(e) => setNewLead((p) => ({ ...p, contactEmail: e.target.value }))} className="border rounded px-3 py-2" />
              <input placeholder="Lead Value *" value={newLead.leadvalue} onChange={(e) => setNewLead((p) => ({ ...p, leadvalue: e.target.value }))} className="border rounded px-3 py-2" />
              <textarea placeholder="Comments" value={newLead.comments} onChange={(e) => setNewLead((p) => ({ ...p, comments: e.target.value }))} className="border rounded px-3 py-2 sm:col-span-2" rows={3}/>
            </div>

            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => setShowNewLead(false)} className="px-4 py-2 rounded border">Cancel</button>
              <button onClick={handleSaveNewLead} className="px-4 py-2 rounded bg-[#6cbb23] text-white">Save Lead</button>
            </div>
          </div>
        </div>
      )}

  {/* local WhatsApp preview modal fallback */}
{localWaOpen && localWaLead && (
  <div className="fixed inset-0 z-60 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl h-[80vh] flex overflow-hidden border border-[#25D366]">
      
   {/* LEFT SIDE - Message Templates */}


<div className="w-1/3 bg-[#E9FCEB] border-r border-gray-300 flex flex-col">
  <div className="p-4 bg-[#25D366] text-white font-semibold text-lg">
    💬 Zafflo Message Templates
  </div>

  <div className="flex-1 overflow-y-auto p-3 text-sm space-y-4">
    {[
      {
        category: "1️⃣ Introduction Messages",
        templates: [
          {
            title: "Intro 1 (Icebreaker)",
            text: `Hi 👋 ${localWaLead.leadname || "there"}, I’m ${
              user?.displayName || email.split("@")[0]
            }. We help hotels digitize room service & restaurant orders. Guests scan a QR, place orders instantly, and staff receives them directly on WhatsApp or kitchen display. No more reception calls 📞. Go live in under 1 minute. First year is free! 👉 Can I show you a quick demo?`,
          },
          {
            title: "Intro 2 (Value Pitch)",
            text: `Hey ${localWaLead.leadname || "there"}, imagine if your guests could order food, towels, or cleaning directly from their phone 📱 — without calling reception! That’s what Zafflo does. Simple QR → Guest orders → WhatsApp alert to staff. I’m ${
              user?.displayName || email.split("@")[0]
            } from Zafflo. Would you like me to show how it looks for your property?`,
          },
        ],
      },
      {
        category: "2️⃣ Follow-up / Demo Messages",
        templates: [
          {
            title: "Follow-up 1 (Feature Summary)",
            text: `Hi ${localWaLead.leadname || "there"}, with Zafflo, your hotel can reduce reception calls, let guests order via QR, and track sales — all free for 1 year. Shall I set up a free trial?`,
          },
          {
            title: "Follow-up 2 (Quick Demo Invite)",
            text: `Hi ${localWaLead.leadname || "there"}, I can give you a 2-minute demo of Zafflo — our QR ordering system for hotels. Guests scan & order, staff gets it on WhatsApp. Shall I share a short demo link?`,
          },
          {
            title: "Follow-up 3 (Post-Demo Check-in)",
            text: `Hi ${localWaLead.leadname || "there"}, just checking if you tried the Zafflo demo. Hotels using it are seeing 30% more orders and happier guests 😊 — ${
              user?.displayName || email.split("@")[0]
            }`,
          },
          {
            title: "Follow-up 4 (Trial Offer)",
            text: `Hi ${localWaLead.leadname || "there"}, Zafflo is offering free setup + 1-year access for new hotels 🎉 No hardware needed. Just WhatsApp. Want to reserve your slot?`,
          },
        ],
      },
      {
        category: "3️⃣ Re-engagement / Exit Messages",
        templates: [
          {
            title: "Polite Exit",
            text: `Whenever you’re ready to try QR-based guest ordering, visit 👉 https://zafflo.com. Have a great day! 🌿`,
          },
          {
            title: "Stay in Touch",
            text: `Hey ${localWaLead.leadname || "there"}, Zafflo helps hotels like yours get repeat guests through smart WhatsApp engagement. Whenever you’re ready, I’ll be happy to help 💬`,
          },
        ],
      },
    ].map((section, i) => (
      <div key={i} className="border rounded-md bg-gray-50 shadow-sm">
        {/* Category Header */}
        <div
          className="flex justify-between items-center p-3 bg-[#6cbb23] text-white cursor-pointer rounded-t-md"
          onClick={() =>
            setOpenCategory(openCategory === i ? null : i)
          }
        >
          <h4 className="font-semibold">{section.category}</h4>
          <span>{openCategory === i ? "−" : "+"}</span>
        </div>

        {/* Compact Template Cards */}
        {openCategory === i && (
          <div className="p-3 space-y-2">
            {section.templates.map((tpl, idx) => (
              <div
                key={idx}
                onClick={() => setSelectedMessage(tpl.text)}
                className="p-3 bg-white rounded-md border hover:bg-[#DFFFE2] cursor-pointer transition-all"
              >
                <h5 className="font-semibold text-gray-800 mb-1 text-sm">
                  {tpl.title}
                </h5>
                <p className="text-gray-600 text-xs whitespace-pre-line">
                  {tpl.text.length > 100
                    ? tpl.text.slice(0, 100) + "..."
                    : tpl.text}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    ))}
  </div>
</div>



      {/* RIGHT SIDE - Chat View */}
      <div className="w-2/3 flex flex-col bg-[#ECE5DD]">
        {/* Header */}
        <div className="flex justify-between items-center p-4 bg-[#075E54] text-white">
          <div>
            <h4 className="font-semibold text-lg">{localWaLead.leadname || "Lead"}</h4>
            <p className="text-sm opacity-80">{localWaLead.mobile}</p>
          </div>
          <button onClick={() => setLocalWaOpen(false)} className="text-white text-xl">✖</button>
        </div>

        {/* Chat Text Area */}
        <div className="flex-1 p-4 overflow-y-auto flex items-start justify-start">
          <textarea
            className="w-full h-full border border-gray-300 rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-[#25D366]"
            placeholder="Select a template or type your message..."
            value={selectedMessage}
            onChange={(e) => setSelectedMessage(e.target.value)}
          />
        </div>

        {/* Footer Buttons */}
        <div className="p-4 bg-white border-t flex justify-between items-center">
          <button
            onClick={() => setLocalWaOpen(false)}
            className="px-4 py-2 border rounded hover:bg-gray-100"
          >
            Close
          </button>
          <a
            href={`https://wa.me/${String(localWaLead.mobile).replace(
              /\D/g,
              ""
            )}?text=${encodeURIComponent(selectedMessage || "")}`}
            target="_blank"
            rel="noreferrer"
            className={`px-5 py-2 rounded text-white font-semibold ${
              selectedMessage
                ? "bg-[#25D366] hover:bg-[#1DA851]"
                : "bg-gray-400 cursor-not-allowed"
            }`}
            onClick={() => {
              if (selectedMessage) setLocalWaOpen(false);
            }}
          >
            Send on WhatsApp
          </a>
        </div>
      </div>
    </div>
  </div>
)}

    </div>
  );
}
